import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, DialogMessage, Text } from '@reservamos/elements';
import insuranceETN from '../../images/brands-unique/etn/insuranceETN.png';
import { formatCurrency } from '../../utils/currency';
import useCurrencyConfig from '../../hooks/whitelabel/useCurrencyConfig';
import useInsuranceConfig from '../../hooks/useInsuranceConfig';
import useWhitelabelEnvs from '../../hooks/whitelabel/useWhitelabelEnvs';

const propTypes = {
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  insuranceDuration: PropTypes.number.isRequired,
  insuranceOutgoingAmount: PropTypes.number,
  insuranceIncomingAmount: PropTypes.number,
};

/**
 * InsuranceDialog component.
 * Render a dialog to offer travel insurance to the user with acceptance/rejection options.
 *
 * The title and the icon resolve by brand. Everything else — the body copy and both
 * buttons — is deliberately shared: product asked for only those two to move.
 *
 * `Passengers.js` renders this for every tenant with `PASSENGERS_INSURANCE_ENABLED`, so
 * unlike the per-passenger card there is no structural guarantee here. What keeps the other
 * brands still is the pair of fallbacks: i18next drops to the context-less key when a brand
 * has no `_<brand>` variant, and `brandIcon` is null for a brand outside the icon map.
 *
 * @param {object} props - onAccept, onReject, insuranceDuration, insuranceOutgoingAmount, insuranceIncomingAmount.
 * @param {Function} props.onAccept - Callback function triggered when user accepts the insurance offer.
 * @param {Function} props.onReject - Callback function triggered when user rejects the insurance offer.
 * @param {number} props.insuranceDuration - Duration of the insurance coverage in hours.
 * @param {number} props.insuranceOutgoingAmount - Insurance amount for outgoing trip (optional).
 * @param {number} props.insuranceIncomingAmount - Insurance amount for incoming/return trip (optional).
 * @returns {JSX.Element} The rendered insurance dialog component.
 */
const InsuranceDialog = ({
  onAccept,
  onReject,
  insuranceDuration,
  insuranceOutgoingAmount,
  insuranceIncomingAmount,
}) => {
  const { t } = useTranslation('passengers');
  const { currency, locale } = useCurrencyConfig();
  const { brand } = useWhitelabelEnvs();
  const { brandIcon } = useInsuranceConfig();
  let insuranceAmount = insuranceOutgoingAmount || 0;
  if (insuranceIncomingAmount) {
    insuranceAmount += insuranceIncomingAmount;
  }

  return (
    <DialogMessage
      iconType={brandIcon || insuranceETN}
      title={t('label.protected_traveler', { context: brand })}
      primaryButton={t('button.yes_thanks')}
      onClickPrimary={onAccept}
      onClickSecondary={onReject}
      secondaryButton={t('button.no_thanks')}
      showFade
      dialogType="accent"
    >
      <Spacing vertical size="S">
        <Text>
          {t('protect_luggage', {
            insurance_amount: formatCurrency(insuranceAmount, 2, currency, locale),
          })}
        </Text>
        <Text>{t('valid_time', { insurance_duration: insuranceDuration })}</Text>
      </Spacing>
    </DialogMessage>
  );
};

InsuranceDialog.propTypes = propTypes;
export default InsuranceDialog;
